/**
 * Slice 5 — Per-session, per-connection `Sent ✓` state for the universal
 * `DatabaseListingFallbackBanner`.
 *
 * After a successful POST of `analytics:send-diagnostic-report` the banner
 * flips to `Sent ✓` for the rest of the session on every surface that
 * renders it for the same `connectionId`. Quitting the app wipes the state
 * (this is in-memory Zustand, not persisted) — the PRD calls this out as
 * "a session signal, not durable state".
 */

import { create } from 'zustand';

type SendStatus = 'idle' | 'sending' | 'sent' | 'failed';

type FallbackBannerSentState = {
  /** `(connectionId)` → status. Resets on quit. */
  statusByConnection: Record<string, SendStatus>;
};

type FallbackBannerSentActions = {
  setStatus: (connectionId: string, status: SendStatus) => void;
  reset: () => void;
};

export const useFallbackBannerSentStore = create<
  FallbackBannerSentState & FallbackBannerSentActions
>((set) => ({
  statusByConnection: {},
  setStatus: (connectionId, status) =>
    set((state) => ({
      statusByConnection: {
        ...state.statusByConnection,
        [connectionId]: status,
      },
    })),
  reset: () => set({ statusByConnection: {} }),
}));

export function useFallbackBannerSendStatus(connectionId: string): SendStatus {
  return useFallbackBannerSentStore(
    (s) => s.statusByConnection[connectionId] ?? 'idle',
  );
}

export type FallbackBannerSendStatus = SendStatus;
